import { Component, HostBinding, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { starAnimations } from '@pay/animations';
import { SingleDateRangeFilter } from 'app/components/app-layout/shared/report-filters/single-date-range/single-date-range-filter';
import { Observable } from 'rxjs';
import { TabularReportColumn, TabularReportRow } from 'app/components/app-layout/shared/tabular-report/tabular-report.config';
import { ReportsService } from 'app/services/reports/reports.service';
import { LegFiJwtService } from 'app/services/auth/legfi-jwt.service';

@UntilDestroy()
@Component({
    selector: 'app-dash-expense-graph',
    templateUrl: './dash-expense-graph.component.html',
    styleUrls: ['./dash-expense-graph.component.scss'],
    animations: starAnimations,
})
export class DashExpenseGraphComponent implements OnInit
{
    @HostBinding('class.card') card = true;
    rows: TabularReportRow[] = [];
    columns: TabularReportColumn[];
    chartTotal: number = 0;
    chartOtherTotal: number = 0;

    catOne: { amount: number, category: string, percent: number, offset: number } = null;
    catTwo: { amount: number, category: string, percent: number, offset: number } = null;
    catThree: { amount: number, category: string, percent: number, offset: number } = null;

    filters: SingleDateRangeFilter;
    filtersLoading = true;
    reportLoading = true;
    hasReadAccess = false;

    constructor(
            private _reportsService: ReportsService,
    ) {
    }

    ngOnInit() {
        this.hasReadAccess = LegFiJwtService.doesUserHaveModulePermission('transactions', false);
    }

    filtersLoaded(filters: SingleDateRangeFilter) {
        this.filtersLoading = false;
        this.filters = filters;
        this.getReport(filters);
    }

    getReport(filters?: SingleDateRangeFilter) {
        if (filters) {
            this.filters = filters;
        }

        const request: Observable<Object> = this._reportsService.getExpensesByCategory(
                false,
                filters,
        );

        this.reportLoading = true;
        request.pipe(untilDestroyed(this)).subscribe(
                (data: Object) => {
                    this.rows = data['rows'];
                    this.columns = data['columns'];

                    this.assembleChartData();

                },
                () => {
                    alert('Unable to load report data.');
                },
        );
    }

    /** Method to take found data and render in a circle graph */
    assembleChartData() {
        if (this.rows && this.rows.length > 0) {
            this.resetAll();

            // last row has totals
            this.chartTotal = this.rows[this.rows.length - 1].amount;

            // row[0] has child with rows of categories and totals
            // const incoming = this.rows[0].children;
            let categories = [];
            if (this.rows.length > 1) {
                // parse out the categories with totals
                categories = this.rows
                        .filter((item) => item.date.indexOf('Total for') === 0)
                        .map(item => {
                            return {
                                amount: item.amount,
                                category: item.date.replace('Total for ', ''),
                            };
                        })
                        .sort((a, b) => {
                            return a.amount > b.amount ? -1 : 1;
                        });

                this.chartOtherTotal = this.chartTotal;
                const percentTotal = this.rows
                        .filter((item, index) => item.date.indexOf('Total for') === 0)
                        .map(c => {
                            if (c.amount < 0) {
                                return c.amount * -1;
                            } else {
                                return c.amount;
                            }
                        }).reduce((acc, val) => {
                            return acc + val;
                        }, 0);

                // only the top 3 amt categories get displayed
                if (categories.length >= 1) {
                    this.catOne = categories[0];
                    this.catOne.percent = (this.catOne.amount / percentTotal) * 440;
                    this.catOne.offset = 440 - this.catOne.percent;
                    this.chartOtherTotal -= this.catOne.amount;
                }
                if (categories.length >= 2) {
                    this.catTwo = categories[1];
                    this.catTwo.percent = (this.catTwo.amount / percentTotal) * 440;
                    this.catTwo.offset = 440 - (this.catTwo.percent + this.catOne.percent);
                    this.chartOtherTotal -= this.catTwo.amount;
                }
                if (categories.length >= 3) {
                    this.catThree = categories[2];
                    this.catThree.percent = (this.catThree.amount / percentTotal) * 440;
                    this.catThree.offset = 440 - (this.catTwo.percent + this.catOne.percent + this.catThree.percent);
                    this.chartOtherTotal -= this.catThree.amount;
                }
            }

            this.reportLoading = false;
        } else {
            this.chartTotal = 0;
            this.chartOtherTotal = 0;
            this.reportLoading = false;
        }
    }

    resetAll() {
        this.chartTotal = 0;
        this.chartOtherTotal = 0;

        this.catOne = null;
        this.catTwo = null;
        this.catThree = null;
    }
}
